import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandL, BrandS } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { droppedCard } from '@sorare/core/src/components/card/style';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { waitMs } from '@sorare/core/src/lib/animation/waitMs';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { CollectionAnimation } from '../CollectionAnimation';
import { AnimatedContentSwap } from './AnimatedContentSwap';
import type {
  PackRecap_anyCardInterface,
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables,
} from './__generated__/index.graphql';

const cardDisappearAnimation = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-120px);
  }
`;
const cardEnterAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(120px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const disappearingStyle = css`
  .disappearing & {
    opacity: 1;
    transform: translateY(0);
    animation: ${cardDisappearAnimation} 0.5s calc(var(--index, 0) * 0.05s)
      cubic-bezier(0.59, 0.02, 0.95, 0.68) forwards;
  }
`;

const Wrapper = styled(Vertical).attrs({ center: true })`
  flex: 1;
  gap: calc(8 * var(--unit));
  padding: var(--quadruple-unit) 0 0;
  min-height: var(--100vh);
  @media ${laptopAndAbove} {
    height: unset;
    min-height: min(80vh, 900px);
  }
  &.collectionAnimationActive {
    max-height: 100vh;
    @media ${laptopAndAbove} {
      height: 85vh;
    }
    overflow: hidden;
  }
`;
const titleStyle = css`
  text-shadow: 0px 0px 4px var(--c-nd-300);
  opacity: 0;
  animation: ${fadeIn} 1s ease-in-out forwards;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--double-unit);
`;
const Title = styled(BrandL)`
  ${titleStyle}
  ${disappearingStyle}
`;
const Subtitle = styled(BrandS)`
  ${titleStyle}
  ${disappearingStyle}
`;
const CardsWrapper = styled(Vertical).attrs({ center: true })`
  flex: 1;
`;
const Cards = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
  max-width: var(--100vw);
  --gap: var(--double-unit);
  gap: var(--gap);
`;
const CardImgWrapper = styled.div`
  width: calc(calc(33 * var(--vw)) - var(--gap));
  @media ${tabletAndAbove} {
    width: 120px;
  }
  & > img {
    max-width: 100%;
    max-height: 25vh;
    aspect-ratio: var(--card-aspect-ratio);
    object-fit: contain;
    position: relative;
  }
  &.dropped {
    ${droppedCard}
  }
  opacity: 0;
  transform: translateY(120px);
  animation: ${cardEnterAnimation} 0.8s calc(var(--index) * 0.05s)
    cubic-bezier(0.02, 0.27, 0.53, 0.98) forwards;
  ${disappearingStyle}
`;
const Footer = styled(Horizontal).attrs({ center: true })`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  justify-content: flex-end;
`;

const CLAIM_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimCardsFromPackMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
      pack {
        id
        cards {
          slug
          ...CollectionAnimation_anyCardInterface
        }
      }
    }
  }
  ${CollectionAnimation.fragments.anyCardInterface}
  ${sportProfile}
` as TypedDocumentNode<
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables
>;

type Props = {
  cards: PackRecap_anyCardInterface[];
  onContinue: () => void;
  droppedCards: DropCardInput[];
  onBack: () => void;
  isFirstPack?: boolean;
  packId: string;
  cardSetSlug: Nullable<string>;
};
export const PackRecap = ({
  cards,
  droppedCards,
  onContinue,
  onBack,
  isFirstPack,
  packId,
  cardSetSlug,
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { enableSeriesOnboardingPacks = false },
  } = useFeatureFlags();

  const [claimCardsFromPack, { loading: claimCardsFromPackLoading }] =
    useMutation(CLAIM_CARDS_FROM_PACK_MUTATION, {
      showErrorsWithSnackNotification: true,
    });
  const [claimCardsFromPackMutionData, setClaimCardsFromPackMutionData] =
    useState<claimCardsFromPackMutation['claimCardsFromPack']>();

  const [disappearing, setDisappearing] = useState(false);
  const keptCards: typeof cards = [];
  const scrappedCards: typeof cards = [];
  cards.forEach(c => {
    if (droppedCards.some(card => card.cardSlug === c.slug)) {
      scrappedCards.push(c);
    } else {
      keptCards.push(c);
    }
  });
  const [collectionAnimationActive, setCollectionAnimationActive] =
    useState(false);

  const newlyKeptCards = keptCards.filter(card => !card.previouslyOwnedCard);

  return (
    <AnimatedContentSwap
      swap={collectionAnimationActive}
      back={
        <CollectionAnimation
          newCards={
            claimCardsFromPackMutionData?.pack?.cards.filter(card =>
              newlyKeptCards.some(c => c.slug === card.slug)
            ) || []
          }
          shouldAnimate={collectionAnimationActive}
          onAnimationEnd={onContinue}
          cardSetSlug={cardSetSlug}
        />
      }
    >
      <Wrapper
        center
        gap={6}
        className={classNames('text-center', {
          collectionAnimationActive,
          disappearing,
        })}
      >
        <Vertical center gap={3} style={{ flex: 1 }}>
          <Title>
            <Vertical center gap={2}>
              <StarBall />
              <FormattedMessage
                id="TinderCardSwipe.title2"
                defaultMessage="{count, plural, one {New signing} other {new signings}}"
                values={{
                  count: cards.length,
                }}
              />
            </Vertical>
          </Title>
          {keptCards.length ? (
            <CardsWrapper>
              <Cards>
                {keptCards.map((card, index) => (
                  <CardImgWrapper
                    key={card.slug}
                    style={{ '--index': index } as React.CSSProperties}
                  >
                    <CardImg src={card.pictureUrl} width={320} />
                  </CardImgWrapper>
                ))}
              </Cards>
            </CardsWrapper>
          ) : (
            <BodyL color="var(--c-nd-600)">
              <FormattedMessage
                id="TinderCardSwipe.noNewSignings2"
                defaultMessage="All players in the pack were scrapped"
              />
            </BodyL>
          )}
        </Vertical>
        {!!scrappedCards.length && (
          <Vertical gap={1.5}>
            <Subtitle>
              <FormattedMessage
                id="TinderCardSwipe.droppedCards"
                defaultMessage="Scrapped Cards"
              />
            </Subtitle>
            <Cards>
              {scrappedCards.map((card, index) => (
                <CardImgWrapper
                  key={card.slug}
                  className={classNames('dropped', {
                    disappearing,
                  })}
                  style={{ '--index': index } as React.CSSProperties}
                >
                  <CardImg src={card.pictureUrl} width={320} />
                </CardImgWrapper>
              ))}
            </Cards>
          </Vertical>
        )}
        <Footer>
          {!isFirstPack && (
            <Button color="quaternary" onClick={onBack} size="medium" fullWidth>
              <FormattedMessage {...glossary.back} />
            </Button>
          )}
          <LoadingButton
            color="secondary"
            onClick={async () => {
              const { data } = await claimCardsFromPack({
                variables: {
                  sport,
                  input: {
                    chosenCardSlugs: keptCards.map(c => c.slug),
                    packId,
                  },
                },
              });
              setClaimCardsFromPackMutionData(data?.claimCardsFromPack);

              setDisappearing(true);
              await waitMs(600 + keptCards.length * 100);
              if (
                (!isFirstPack || enableSeriesOnboardingPacks) &&
                newlyKeptCards.length > 0
              ) {
                setCollectionAnimationActive(true);
              } else {
                onContinue();
              }
            }}
            size="medium"
            loading={claimCardsFromPackLoading}
            fullWidth
          >
            <FormattedMessage {...glossary.continue} />
          </LoadingButton>
        </Footer>
      </Wrapper>
    </AnimatedContentSwap>
  );
};

PackRecap.fragments = {
  anyCardInterface: gql`
    fragment PackRecap_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      previouslyOwnedCard
    }
  ` as TypedDocumentNode<PackRecap_anyCardInterface>,
};
